import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileSignature,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Shield,
  Download,
  Send,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Service Agreement
 * 
 * Step 5 of the Intake Workflow
 * Digital service agreement with e-signature capability
 */

export default function ServiceAgreement() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  
  // Signature state
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw'); // draw or type
  const [typedName, setTypedName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchData();
  }, [requestId]);

  useEffect(() => {
    if (canvasRef.current && signatureMode === 'draw') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#0D1B33';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [signatureMode]);

  const fetchData = async () => {
    try {
      // Fetch service request
      const reqRes = await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
        credentials: 'include'
      });
      if (reqRes.ok) {
        const reqData = await reqRes.json();
        setRequest(reqData);
        
        // Pre-fill typed name from representative
        if (reqData.representative_name) {
          setTypedName(reqData.representative_name);
        }
      }

      // Fetch care plan
      const planRes = await fetch(`${API_URL}/api/intake/care-plans?service_request_id=${requestId}&limit=1`, {
        credentials: 'include'
      });
      if (planRes.ok) {
        const planData = await planRes.json();
        if (planData.items && planData.items.length > 0) {
          setCarePlan(planData.items[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing handlers
  const startDrawing = (e) => {
    if (signatureMode !== 'draw') return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || signatureMode !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signatureMode === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    } else {
      setTypedName('');
    }
  };

  const getSignatureData = () => {
    if (signatureMode === 'draw' && canvasRef.current) {
      return canvasRef.current.toDataURL('image/png');
    } else if (signatureMode === 'type' && typedName) {
      // Create a canvas with the typed name in cursive
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 32px "Brush Script MT", cursive';
      ctx.fillStyle = '#0D1B33';
      ctx.textAlign = 'center';
      ctx.fillText(typedName, 200, 60);
      return canvas.toDataURL('image/png');
    }
    return null;
  };

  const handleSign = async () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the terms to continue');
      return;
    }

    if (signatureMode === 'draw' && !hasSignature) {
      toast.error('Please provide your signature');
      return;
    }

    if (signatureMode === 'type' && !typedName.trim()) {
      toast.error('Please type your name');
      return;
    }

    setSaving(true);
    try {
      const signatureData = getSignatureData();
      
      // Update service request with signature and status
      const res = await fetch(`${API_URL}/api/intake/service-requests/${requestId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_signature: {
            data: signatureData,
            method: signatureMode,
            typed_name: signatureMode === 'type' ? typedName : null,
            signed_at: new Date().toISOString(),
            ip_address: 'captured-server-side',
            user_agent: navigator.userAgent,
          },
          status: 'agreement_signed',
        })
      });

      if (res.ok) {
        toast.success('Agreement signed successfully!');
        // Navigate to caregiver matching
        navigate(`/intake/match/${requestId}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save signature');
      }
    } catch (err) {
      console.error('Error signing agreement:', err);
      toast.error('Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#176DFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="mb-6">
          <button
            onClick={() => navigate('/organization/data-entry')}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0D1B33] mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to CareHub
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <FileSignature className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#0D1B33]">Service Agreement</h1>
              <p className="text-sm text-[#64748B]">
                Step 5 of 6 — {request?.client_name}
              </p>
            </div>
          </div>
        </header>

        {/* No Care Plan Warning */}
        {!carePlan && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900">Care Plan Required</h3>
                <p className="text-amber-700 mt-1">
                  Please complete the care plan before signing the service agreement.
                </p>
                <Button
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate(`/intake/care-plan/${requestId}`)}
                >
                  Create Care Plan
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Agreement Content */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
            <h2 className="font-semibold text-[#0D1B33]">A Touch of Angels Home Care Agency</h2>
            <p className="text-sm text-[#64748B]">Service Agreement</p>
          </div>
          <div className="p-6 prose prose-sm max-w-none">
            <h3 className="text-[#0D1B33]">Service Agreement</h3>
            
            <p className="text-[#64748B]">
              This Service Agreement (&quot;Agreement&quot;) is entered into by and between A Touch of Angels Home Care Agency 
              (&quot;Agency&quot;) and the undersigned client or authorized representative (&quot;Client&quot;).
            </p>

            <h4 className="text-[#0D1B33] mt-6">1. Services</h4>
            <p className="text-[#64748B]">
              The Agency agrees to provide non-medical home care services to {request?.client_name} as outlined 
              in the Care Plan. Services may include assistance with activities of daily living (ADLs), 
              instrumental activities of daily living (IADLs), companionship, and other agreed-upon services.
            </p>

            {carePlan && (
              <div className="bg-[#F8FAFC] rounded-lg p-4 my-4">
                <h5 className="font-medium text-[#0D1B33] mb-2">Care Plan Summary</h5>
                <ul className="text-sm text-[#64748B] space-y-1">
                  <li><strong>Weekly Hours:</strong> {carePlan.total_hours_per_week || 'TBD'}</li>
                  <li><strong>Start Date:</strong> {carePlan.start_date || 'TBD'}</li>
                  <li><strong>ADL Services:</strong> {carePlan.adl_needs?.length || 0}</li>
                  <li><strong>IADL Services:</strong> {carePlan.iadl_needs?.length || 0}</li>
                </ul>
              </div>
            )}

            <h4 className="text-[#0D1B33] mt-6">2. Payment Terms</h4>
            <p className="text-[#64748B]">
              Payment for services shall be made according to the selected payment method: 
              <strong className="capitalize"> {request?.payment_type?.replace('_', ' ') || 'Private Pay'}</strong>.
              For private pay clients, payment is due upon receipt of invoice. For insurance or VA/TriWest clients, 
              the Agency will bill the appropriate payer directly.
            </p>

            <h4 className="text-[#0D1B33] mt-6">3. Cancellation Policy</h4>
            <p className="text-[#64748B]">
              Either party may cancel a scheduled visit with at least 24 hours notice. 
              Visits cancelled with less than 24 hours notice may be subject to a cancellation fee.
            </p>

            <h4 className="text-[#0D1B33] mt-6">4. Client Rights</h4>
            <p className="text-[#64748B]">
              The Client has the right to be treated with dignity and respect, to participate in care planning, 
              to privacy of personal and medical information, and to voice concerns without fear of retaliation.
            </p>

            <h4 className="text-[#0D1B33] mt-6">5. Liability</h4>
            <p className="text-[#64748B]">
              The Agency maintains liability insurance. The Agency is not responsible for any pre-existing 
              conditions or injuries not caused by the Agency&apos;s negligence.
            </p>
          </div>
        </div>

        {/* HIPAA Notice */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Privacy Notice</h3>
              <p className="text-sm text-blue-700 mt-1">
                Your personal and health information is protected under HIPAA. We will only use and disclose 
                your information as necessary to provide your care and as permitted by law.
              </p>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-[#E2E8F0] bg-gray-50">
            <h2 className="font-semibold text-[#0D1B33]">Electronic Signature</h2>
          </div>
          <div className="p-6">
            {/* Signature Mode Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSignatureMode('draw')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  signatureMode === 'draw'
                    ? 'bg-[#176DFF] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Draw Signature
              </button>
              <button
                onClick={() => setSignatureMode('type')}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  signatureMode === 'type'
                    ? 'bg-[#176DFF] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Type Name
              </button>
            </div>

            {/* Signature Canvas */}
            {signatureMode === 'draw' && (
              <div className="relative mb-4">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={150}
                  className="w-full border-2 border-dashed border-[#E2E8F0] rounded-lg bg-white cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {!hasSignature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-[#94A3B8]">Sign here</span>
                  </div>
                )}
              </div>
            )}

            {/* Typed Signature */}
            {signatureMode === 'type' && (
              <div className="mb-4">
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your full legal name"
                  className="w-full px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#176DFF]/20 focus:border-[#176DFF]"
                />
                {typedName && (
                  <div className="mt-3 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                    <p className="text-2xl italic" style={{ fontFamily: '"Brush Script MT", cursive' }}>
                      {typedName}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Clear Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={clearSignature}
              className="mb-4"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear
            </Button>

            {/* Agreement Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer p-4 bg-[#F8FAFC] rounded-lg">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#CBD5E1] text-[#176DFF] focus:ring-[#176DFF]"
              />
              <div>
                <span className="text-sm font-medium text-[#0D1B33]">
                  I agree to the terms of this Service Agreement
                </span>
                <p className="text-xs text-[#64748B] mt-1">
                  By signing, I acknowledge that I have read and understand the terms above. 
                  I am the client or authorized representative with the legal authority to sign this agreement.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate(`/intake/care-plan/${requestId}`)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Care Plan
          </Button>
          <div className="flex gap-3">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-1" />
              Download PDF
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSign}
              disabled={saving || !agreedToTerms || (signatureMode === 'draw' && !hasSignature) || (signatureMode === 'type' && !typedName.trim())}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Sign Agreement
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
